import type { RouteLocationRaw } from 'vue-router'
import * as Sentry from '@sentry/nuxt'

export default function useAuth() {
  const account = useState<components['schemas']['Account'] | null>(
    'auth_account',
    () => null
  )
  const collaborator = useState<components['schemas']['Collaborator'] | null>(
    'auth_collaborator',
    () => null
  )
  const isAuthenticated = computed<boolean>(() => !!account.value)

  const hasModulesAccess = (
    modules: components['schemas']['ModulesEnum'][],
    some?: boolean
  ) => !modules?.length || modules[some ? 'some' : 'every'](hasModuleAccess)

  const hasModuleAccess = (module: components['schemas']['ModulesEnum']) => {
    const { currentOrganization: organization } = useOrganization()
    if (
      organization.value === null ||
      account.value === null ||
      collaborator.value === null
    )
      return false

    if (account.value.is_staff || account.value.is_superuser) return true
    if (organization.value.user.id === account.value.id) return true
    if (collaborator.value.is_admin) return true

    let collaboratorModules: components['schemas']['ModulesEnum'][] = []
    if (collaborator.value.is_admin) {
      collaboratorModules = Object.keys(
        ModuleName
      ) as components['schemas']['ModulesEnum'][]
    } else if (collaborator.value.modules) {
      collaboratorModules = collaborator.value.modules
    }
    return collaboratorModules.includes(module)
  }

  const isAdmin = () => {
    const { currentOrganization: organization } = useOrganization()
    if (
      organization.value === null ||
      account.value === null ||
      collaborator.value === null
    )
      return false

    if (account.value.is_staff || account.value.is_superuser) return true
    if (organization.value.user.id === account.value.id) return true
    return collaborator.value.is_admin
  }

  const logout = async (to?: RouteLocationRaw) => {
    const { $api, $umami } = useNuxtApp()
    await $api('/account/logout/', {
      method: 'POST',
    }).then(() => {
      account.value = null
      sessionStorage.clear()

      Sentry.setUser(null)
      $umami.identify()

      navigateTo(to ?? { name: 'account-login' })
    })
  }

  const setAccount = async () => {
    const { $api, $umami } = useNuxtApp()
    await $api<components['schemas']['Account']>('/account/')
      .then((response) => {
        account.value = response

        const userPayload = {
          id: response.id,
          email: response.email,
          username: response.email,
        }

        Sentry.setUser(userPayload)
        $umami.identify(userPayload)
      })
      .catch(() => (account.value = null))
  }

  const setCollaborator = async () => {
    const { $api } = useNuxtApp()
    await $api<components['schemas']['Collaborator']>('/collaborator/')
      .then((response) => (collaborator.value = response))
      .catch(() => (collaborator.value = null))
  }

  return {
    account,
    collaborator,
    hasModulesAccess,
    hasModuleAccess,
    isAdmin,
    isAuthenticated,
    logout,
    setAccount,
    setCollaborator,
  }
}
